import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Search, User, Pencil, Trash2, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AppointmentFormValues } from '@/types/appointment-form';

/**
 * Some emergency contacts arrive with `relationship` populated as a UUID
 * (backend ships an unresolved value-set id when the relation lookup
 * misses). Hide UUID-shaped strings rather than rendering them raw —
 * Flutter falls back to an empty placeholder for these too.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function friendlyRelationship(value: string | undefined | null): string {
  const v = (value ?? '').trim();
  if (!v || UUID_RE.test(v)) return '';
  return v;
}

interface CallbackContactSectionProps {
  /** Hook for "Create Contact" — opens the (TODO) inline contact editor. */
  onCreateContact?: () => void;
}

/**
 * CallbackContactSection — picks the emergency contact to call about
 * this appointment. Mirrors Flutter `callback_contact_widget.dart`.
 *
 * Visible only after a patient is loaded; the contact list comes from
 * `PreBookSelectedPatient.patient.emergencyContactResponse`
 * (Flutter create_appointment_widget.dart:2326–2327). Older payloads
 * use `emergencyContactDataList` — the patient-section mapper accepts
 * both keys at root or nested. Falls back to a helpful empty state
 * when the patient has no contacts on file.
 */
export function CallbackContactSection({ onCreateContact }: CallbackContactSectionProps) {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const patientId = watch('patientId');
  const contacts = watch('emergencyContacts');
  const selectedContactId = watch('callbackContactId');

  const [searchText, setSearchText] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const COLLAPSED_COUNT = 4;

  if (!patientId) return null;

  const filtered = contacts.filter((c) => {
    if (!searchText) return true;
    const q = searchText.toLowerCase();
    return (
      (c.name ?? '').toLowerCase().includes(q) ||
      (c.relationship ?? '').toLowerCase().includes(q) ||
      (c.phone ?? '').toLowerCase().includes(q)
    );
  });

  const initials = (name: string | undefined) =>
    (name ?? '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0])
      .join('')
      .toUpperCase();

  // Selected-state card — Flutter `callback_contact_widget.dart:115–192`.
  // Replaces the search + grid once a contact is chosen; pencil / trash
  // mirror the Flutter edit + delete actions.
  const selectedContact = selectedContactId
    ? contacts.find((c) => c.id === selectedContactId) ?? null
    : null;

  if (selectedContact) {
    return (
      <div className="px-4 py-3 border-t border-border-subtle">
        <p className="text-xs font-medium text-text-muted mb-2">
          Callback contact
        </p>
        <div className="flex items-center gap-3 p-3 rounded-lg border border-border-default bg-surface-card">
          <Avatar className="h-8 w-8 flex-shrink-0">
            <AvatarImage src={selectedContact.photo ?? undefined} />
            <AvatarFallback className="text-size-2xs font-semibold bg-blue-100 text-brand-depth">
              {initials(selectedContact.name) || <User className="h-4 w-4" />}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-text-primary truncate leading-snug">
              {selectedContact.name ?? '—'}
            </p>
            {selectedContact.phone ? (
              <p className="text-xs text-text-muted truncate leading-snug">
                {selectedContact.phone}
              </p>
            ) : null}
            <p className="text-xs text-text-muted truncate leading-snug">
              {friendlyRelationship(selectedContact.relationship)
                || (selectedContact.isSelf ? 'Self' : '')}
            </p>
          </div>
          <button
            type="button"
            onClick={onCreateContact}
            className="p-1 hover:bg-grey-100 rounded flex-shrink-0"
            aria-label="Edit contact"
          >
            <Pencil className="h-4 w-4 text-brand-primary" />
          </button>
          <button
            type="button"
            onClick={() => setValue('callbackContactId', null, { shouldValidate: true })}
            className="p-1 hover:bg-grey-100 rounded flex-shrink-0"
            aria-label="Clear contact"
          >
            <Trash2 className="h-4 w-4 text-status-error" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-3 border-t border-border-subtle">
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-medium text-text-muted">
          Callback contact
        </p>
        {onCreateContact ? (
          <button
            type="button"
            onClick={onCreateContact}
            className="text-sm font-medium text-brand-primary hover:text-interactive-hover"
          >
            Create Contact
          </button>
        ) : null}
      </div>
      <p className="text-xs text-text-muted mb-3 leading-snug">
        Select a contact from the patient&apos;s community that should be contacted in case of any questions.
      </p>

      <div className="relative mb-3">
        <Search className="absolute left-3 top-3 h-4 w-4 text-text-hint pointer-events-none" />
        <input
          placeholder="Search community"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="w-full h-10 pl-8 pr-3 rounded-lg border border-border-default bg-grey-100 text-sm text-text-primary placeholder:text-text-hint focus:border-brand-primary focus:bg-surface-card focus:outline-none"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-text-muted py-2">
          {searchText ? 'No contacts match.' : 'No contacts on file for this patient.'}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-x-3 gap-y-3">
            {(isExpanded ? filtered : filtered.slice(0, COLLAPSED_COUNT)).map((c, i) => (
              <button
                key={c.id ?? `contact-${i}`}
                type="button"
                onClick={() => setValue('callbackContactId', c.id ?? null, { shouldValidate: true })}
                className={cn(
                  'flex items-start gap-2 text-left p-1 rounded-md transition-colors hover:bg-grey-100',
                )}
              >
                <Avatar className="h-8 w-8 flex-shrink-0">
                  <AvatarImage src={c.photo ?? undefined} />
                  <AvatarFallback className="text-size-xs font-semibold bg-blue-100 text-brand-depth">
                    {initials(c.name) || <User className="h-4 w-4" />}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-text-primary truncate leading-snug">
                    {c.name ?? '—'}
                  </p>
                  <p className="text-xs text-text-muted truncate leading-snug">
                    {friendlyRelationship(c.relationship)
                      || (c.isSelf ? 'Self' : '')}
                  </p>
                  {c.phone ? (
                    <p className="text-xs text-text-muted truncate leading-snug">
                      {c.phone}
                    </p>
                  ) : null}
                </div>
              </button>
            ))}
          </div>
          {filtered.length > COLLAPSED_COUNT ? (
            <button
              type="button"
              onClick={() => setIsExpanded((v) => !v)}
              className="mt-2 mx-auto flex items-center justify-center w-8 h-8 rounded-full text-brand-primary hover:bg-blue-100"
              aria-label={isExpanded ? 'Show less' : 'Show more contacts'}
              aria-expanded={isExpanded}
            >
              <ChevronDown
                className={cn('h-5 w-5 transition-transform', isExpanded && 'rotate-180')}
              />
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}
